import React, { useState, useEffect, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";

import { Modal, Input, Button, IconButton } from "../ui";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Icons
const DeleteIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const schema = yup.object().shape({
  name: yup
    .string()
    .min(2, "Nome muito curto")
    .max(50, "Nome muito longo")
    .required("Nome é obrigatório"),
  number: yup.string().min(8, "Número muito curto").max(50, "Número muito longo"),
  email: yup.string().email("E-mail inválido"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      number: "",
      email: "",
      extraInfo: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "extraInfo",
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        reset({
          name: initialValues.name || "",
          number: initialValues.number || "",
          email: initialValues.email || "",
          extraInfo: initialValues.extraInfo || [],
        });
        return;
      }

      if (!contactId) {
        reset({
          name: "",
          number: "",
          email: "",
          extraInfo: [],
        });
        return;
      }

      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (isMounted.current) {
          reset({
            name: data.name || "",
            number: data.number || "",
            email: data.email || "",
            extraInfo: data.extraInfo || [],
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchContact();
    }
  }, [contactId, open, initialValues, reset]);

  const handleClose = () => {
    reset({
      name: "",
      number: "",
      email: "",
      extraInfo: [],
    });
    onClose();
  };

  const handleSaveContact = async (data) => {
    setLoading(true);
    try {
      if (contactId) {
        await api.put(`/contacts/${contactId}`, data);
        handleClose();
      } else {
        const { data: newContact } = await api.post("/contacts", data);
        if (onSave) {
          onSave(newContact);
        }
        handleClose();
      }
      toast.success(i18n.t("contactModal.success"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        contactId
          ? i18n.t("contactModal.title.edit")
          : i18n.t("contactModal.title.add")
      }
      size="lg"
      footer={
        <>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            form="contact-form"
            loading={loading}
            disabled={loading}
          >
            {contactId
              ? i18n.t("contactModal.buttons.okEdit")
              : i18n.t("contactModal.buttons.okAdd")}
          </Button>
        </>
      }
    >
      <form
        id="contact-form"
        onSubmit={handleSubmit(handleSaveContact)}
        className="space-y-4"
      >
        {/* Main Info Section */}
        <div>
          <h3 className="text-sm font-medium text-dark-700 dark:text-dark-300 mb-3">
            {i18n.t("contactModal.form.mainInfo")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={i18n.t("contactModal.form.name")}
              {...register("name")}
              error={errors.name?.message}
              autoFocus
            />
            <Input
              label={i18n.t("contactModal.form.number")}
              {...register("number")}
              error={errors.number?.message}
              placeholder="5513912344321"
            />
          </div>
          <div className="mt-4">
            <Input
              label={i18n.t("contactModal.form.email")}
              {...register("email")}
              error={errors.email?.message}
              placeholder="email@exemplo.com"
              type="email"
            />
          </div>
        </div>

        {/* Extra Info Section */}
        <div>
          <h3 className="text-sm font-medium text-dark-700 dark:text-dark-300 mb-3">
            {i18n.t("contactModal.form.extraInfo")}
          </h3>

          {fields.length > 0 && (
            <div className="space-y-3">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-3">
                  <Input
                    {...register(`extraInfo.${index}.name`)}
                    placeholder={i18n.t("contactModal.form.extraName")}
                    className="flex-1"
                  />
                  <Input
                    {...register(`extraInfo.${index}.value`)}
                    placeholder={i18n.t("contactModal.form.extraValue")}
                    className="flex-1"
                  />
                  <IconButton
                    size="small"
                    color="danger"
                    onClick={() => remove(index)}
                  >
                    <DeleteIcon className="w-4 h-4" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="outlined"
            color="primary"
            fullWidth
            className="mt-3"
            onClick={() => append({ name: "", value: "" })}
            leftIcon={<PlusIcon className="w-4 h-4" />}
          >
            {i18n.t("contactModal.buttons.addExtraInfo")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ContactModal;
